import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';

import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';

import {
  PATIENT_PER_PAGE_OPTIONS,
  usePatientListStore,
} from '../store/patient-list.store';
import type { PatientStatusFilter } from '../types/patient.types';

// PRD §3.2: search is debounced 300 ms; Enter / icon-click trigger immediately.
const SEARCH_DEBOUNCE_MS = 300;

// Status filter enum = canonical 3 states + "all" (CONFLICT_013_01 / VERIFICATION_FEATURE_013_FRONTEND_02):
// 종료예정 ('ending', scheduled-to-close) was deleted in PRD-02 v1.1 and MUST be dropped from the filter —
// canonical ACTIVE / CLOSED / WITHDRAWN only (latest doc wins).
const STATUS_OPTIONS: readonly PatientStatusFilter[] = [
  'all',
  'ACTIVE',
  'CLOSED',
  'WITHDRAWN',
] as const;

export function PatientListFilters() {
  const { t } = useTranslation('admin');
  const filters = usePatientListStore((s) => s.filters);
  const perPage = usePatientListStore((s) => s.perPage);
  const setFilters = usePatientListStore((s) => s.setFilters);
  const setPerPage = usePatientListStore((s) => s.setPerPage);

  // Local input state drives instant typing feedback; the store (and therefore
  // the list query) only updates after the trailing debounce. Status / perPage
  // stay immediate — they are discrete clicks.
  const [searchLocal, setSearchLocal] = useState(filters.search);
  const debouncedSearch = useDebouncedValue(searchLocal, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    setSearchLocal(filters.search);
  }, [filters.search]);

  useEffect(() => {
    if (debouncedSearch !== filters.search) {
      setFilters({ search: debouncedSearch });
    }
  }, [debouncedSearch]);

  // Enter / search-icon click → flush the current input immediately, bypassing
  // the debounce. Matches prototype behavior.
  const flushSearch = () => {
    if (searchLocal !== filters.search) setFilters({ search: searchLocal });
  };

  return (
    <div className="filter-bar">
      <div className="filter-bar__search" style={{ flex: 1 }}>
        <input
          type="text"
          className="filter-bar__search-input"
          placeholder={t('patientManagement.filters.searchPlaceholder')}
          maxLength={50}
          value={searchLocal}
          onChange={(e) => setSearchLocal(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') flushSearch();
          }}
        />
        <button
          type="button"
          onClick={flushSearch}
          className="filter-bar__search-icon-button"
          aria-label={t('patientManagement.filters.searchButton')}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            color: 'var(--muted-foreground)',
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          <Search className="filter-bar__search-icon" size={16} />
        </button>
      </div>

      <select
        className="filter-bar__select"
        style={{ width: 180 }}
        value={filters.status}
        onChange={(e) =>
          setFilters({ status: e.target.value as PatientStatusFilter })
        }
      >
        {STATUS_OPTIONS.map((s) => (
          <option key={s} value={s}>
            {t(`patientManagement.statusFilters.${s}`)}
          </option>
        ))}
      </select>

      <select
        className="filter-bar__select"
        style={{ width: 140 }}
        value={perPage}
        onChange={(e) => setPerPage(Number(e.target.value))}
      >
        {PATIENT_PER_PAGE_OPTIONS.map((n) => (
          <option key={n} value={n}>
            {t('patientManagement.filters.itemsPerPage', { count: n })}
          </option>
        ))}
      </select>
    </div>
  );
}
